/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShieldCheck, Banknote, FileText, Scale } from "lucide-react";
import Layout from "../components/Layout";
import InquiryForm from "../components/InquiryForm";

/**
 * Editorial briefing—"Professional pilots": what the term means.
 *
 * This is the first article served to visitors who indicate they are
 * sourcing pilots. It frames the term through the lens of insurance
 * underwriting and the cost-of-business reality, distinguishing the
 * Part 91 owner-operator structure from Part 135 commercial operations.
 *
 * Hand-written long-form content—not AI-seeded—because the language
 * here is regulatory, and precision matters more than coverage.
 */
const ProfessionalPilots = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      {/* Header / hero */}
      <article className="max-w-[1500px] mx-auto px-6 lg:px-12 pt-16 md:pt-24">
        <Link
          to="/"
          className="text-[13px] font-medium text-[var(--slate)] hover:text-[var(--white)] transition-colors inline-flex items-center gap-2 mb-12"
          data-testid="pp-back"
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <div
          className="font-mono-label mb-6"
          style={{ color: "var(--blue-soft)" }}
        >
          Briefing · Crew sourcing
        </div>

        <h1
          className="font-display-lg text-[var(--white)] leading-[1.04] max-w-5xl"
          style={{ fontSize: "clamp(38px, 5.2vw, 72px)" }}
        >
          "Professional pilots" —
          <br />
          <span className="text-[var(--slate)]">
            what the term means.
          </span>
        </h1>

        <p
          className="mt-10 max-w-2xl"
          style={{
            color: "var(--ivory-dim)",
            fontSize: "18px",
            lineHeight: 1.7,
          }}
        >
          An insurance underwriter's vocabulary, repurposed by aircraft
          owners. A short brief on what owners are actually asking for when
          they ask for "professional pilots," and what that costs.
        </p>

        <div
          className="mt-12 flex items-center gap-6 text-[12px]"
          style={{ color: "var(--slate-dim)", letterSpacing: "0.04em" }}
        >
          <span>Steck Aviation Management</span>
          <span className="h-3 w-px bg-[var(--slate-dim)] opacity-40" />
          <span>Issue No. 01 · Crew</span>
          <span className="h-3 w-px bg-[var(--slate-dim)] opacity-40" />
          <span>~ 7 minute read</span>
        </div>
      </article>

      {/* Body */}
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        {/* Side rail */}
        <aside className="md:col-span-3">
          <div className="md:sticky md:top-32">
            <div
              className="font-mono-label mb-5"
              style={{ color: "var(--slate-dim)" }}
            >
              In this brief
            </div>
            <ol className="space-y-3 text-[13px]" style={{ color: "var(--slate)" }}>
              {[
                "Why the language matters",
                "The underwriter's math",
                "What this actually costs",
                "Who pays whom",
                "What we will not do",
              ].map((s, i) => (
                <li key={s} className="flex gap-3">
                  <span
                    className="font-mono-label flex-shrink-0"
                    style={{ color: "var(--slate-dim)" }}
                  >
                    0{i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </aside>

        {/* Article body */}
        <main className="md:col-span-9 space-y-20">
          <Section
            n="01"
            icon={FileText}
            title="Why the language matters."
            lede="Owners who fly turbine or jet equipment quickly run into the phrase “professional pilots.” The phrase is not a regulatory category. It is an insurance category—and that distinction governs everything that follows."
          >
            <p>
              In Part 91—the regulatory frame for owner-operated, non-commercial
              flying—there is no licensure tier called <em>professional pilot.</em>{" "}
              The FAA recognizes airline transport pilots, commercial pilots, private
              pilots, sport pilots. None of those terms map cleanly to what owners,
              brokers, and underwriters mean when they ask for a “pro crew.”
            </p>
            <p>
              The term is shorthand. It describes a pilot whose training, recurrent
              currency, type rating, and hours-in-type meet the standard an aviation
              insurance underwriter is willing to write a policy against—at a
              premium an owner is willing to pay. That is the operational definition.
              Everything else is marketing.
            </p>
            <p>
              We use the term carefully for that reason. When we say we source
              professional pilots, we are not describing a Part 135 commercial flight
              operation. We are describing the search for pilots an underwriter will
              accept on the owner's policy.
            </p>
          </Section>

          <Section
            n="02"
            icon={Scale}
            title="The underwriter's math."
            lede="An underwriter will quote an owner's policy on a small number of variables. None of them are subjective."
          >
            <p>
              The variables that move the premium for a turbine or jet are
              consistent across the major underwriting markets:
            </p>
            <ul className="article-list">
              <li>
                <strong>Type rating in the make and model.</strong> Not "jet
                experience." Type-specific. A BE-400 type rating is required to
                command a Beechjet 400A.
              </li>
              <li>
                <strong>Total time and hours-in-type.</strong> 5,000 / 500 is a
                common threshold for second-tier jets; 7,500 / 1,000 for heavier
                equipment. Below those thresholds, the policy is either declined
                or written with sharp restrictions.
              </li>
              <li>
                <strong>Recurrent training.</strong> A FlightSafety or CAE schoolhouse
                event within the last twelve months is effectively a precondition.
                Initial training counts only once.
              </li>
              <li>
                <strong>Currency.</strong> Recent landings, instrument approaches,
                and night currency tracked against the policy's open warranties.
              </li>
              <li>
                <strong>Adverse history.</strong> A reportable incident, a
                checkride bust, a sworn statement on the insurance application—any
                of these can move a premium by a multiple, or close the market entirely.
              </li>
            </ul>
            <p>
              When an owner asks for a "professional pilot," they are asking for
              a pilot whose profile crosses all of those thresholds. The phrase is
              the question; the underwriter's binder is the answer.
            </p>
          </Section>

          <Section
            n="03"
            icon={Banknote}
            title="What this actually costs."
            lede="The economics of insurance-acceptable crew are explicit. Owners deserve a written breakdown before they enter into anything."
          >
            <p>
              For a single-aircraft, owner-operated turbine or light jet on a
              Part 91 footing, the realistic annual crew cost in 2026 falls in a
              defined range. The numbers below are intended as orientation, not
              quotation—every operation is priced individually.
            </p>
            <div className="article-table">
              <div className="article-table-row article-table-header">
                <div>Line item</div>
                <div>Range</div>
                <div>Notes</div>
              </div>
              <div className="article-table-row">
                <div>Captain salary</div>
                <div>$140K – $210K</div>
                <div>Type-rated, current, recurrent within 12 months.</div>
              </div>
              <div className="article-table-row">
                <div>First officer (if seated)</div>
                <div>$95K – $145K</div>
                <div>Not required by reg; required by insurance on some jets.</div>
              </div>
              <div className="article-table-row">
                <div>Recurrent training (annual)</div>
                <div>$22K – $38K / pilot</div>
                <div>FlightSafety, CAE, or equivalent.</div>
              </div>
              <div className="article-table-row">
                <div>Health, disability, ancillary</div>
                <div>$14K – $26K / pilot</div>
                <div>Typically through the owner's benefits structure.</div>
              </div>
              <div className="article-table-row">
                <div>Per-diem and travel</div>
                <div>$8K – $22K / pilot</div>
                <div>Function of mission profile and away-from-base days.</div>
              </div>
            </div>
            <p>
              For a single-pilot or low-utilization owner, contract-pilot pricing
              (day-rate plus overnight) typically lands between $1,500 and $2,400
              per duty day, before training, travel, and insurance considerations.
              Contract economics tilt favorable below roughly 180 flight hours per
              year and unfavorable above it. We will model both sides on request.
            </p>
          </Section>

          <Section
            n="04"
            icon={ShieldCheck}
            title="Who pays whom."
            lede="In a Part 91 owner-operated structure, the owner pays the pilot—directly, or through a structure the owner's counsel selects."
          >
            <p>
              Steck Aviation Management is a management services company, not an
              employer of record for the crew. The pilots we source are not on the
              Steck payroll. They are either employed directly by the owner, by the
              ownership entity that holds the aircraft, or by a third-party
              employer-of-record structure the owner's counsel and accountant
              recommend.
            </p>
            <p>
              Our fee is the management fee in our written agreement. We do not
              earn a markup on pilot time. We do not earn a commission on training,
              uniforms, headsets, recurrent events, or any other line item the
              crew touches. Every dollar an owner spends on the crew flows to the
              pilots, the training providers, or the benefits carriers—not to us.
            </p>
            <p>
              This is deliberate. Compensation structures that align a management
              company with the size of the operating bill are precisely the
              structures that produce the abuses owners complain about. Ours does
              not.
            </p>
          </Section>

          <Section
            n="05"
            icon={Scale}
            title="What we will not do."
            lede="The pre-Part-135 stage is a defined posture, not a temporary one. The line is bright."
          >
            <p>
              Until Steck Aviation Management holds a Part 135 air-carrier certificate
              in its own name, we will not:
            </p>
            <ul className="article-list">
              <li>Offer charter or on-demand flights to third parties.</li>
              <li>
                Hold operational control. The owner decides when, where, and
                whether the aircraft flies; we coordinate around those decisions.
              </li>
              <li>
                Position the pilots we source as our pilots. They are the
                owner's pilots. We help find them, vet them, and onboard them.
              </li>
              <li>
                Earn commissions on aircraft transactions, fuel, parts, maintenance
                labor, training events, or pilot compensation.
              </li>
              <li>
                Sell or facilitate any fractional, jet-card, or partial-ownership
                program.
              </li>
            </ul>
            <p>
              We are explicit about which side of the line we are on at any
              given time. Our disclosures page —{" "}
              <Link to="/important-facts" className="article-link">
                important facts
              </Link>{" "}
             —sets the same boundary in writing.
            </p>
          </Section>

          {/* Editorial close */}
          <div
            className="border-t pt-12 mt-12"
            style={{ borderColor: "rgba(255,255,255,0.10)" }}
          >
            <h3
              className="font-display"
              style={{
                color: "var(--white)",
                fontSize: "clamp(26px, 3vw, 36px)",
                lineHeight: 1.18,
              }}
            >
              If this clarifies the question, we should talk.
            </h3>
            <p
              className="mt-5 max-w-2xl"
              style={{ color: "var(--slate)", fontSize: "16px", lineHeight: 1.8 }}
            >
              Crew sourcing under an insurance-acceptable standard is what we do.
              Tell us what you fly, where the aircraft is based, and what your
              underwriter has said so far. We will respond directly.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#inquiry" className="btn-primary">
                Speak with us <ArrowRight size={14} />
              </a>
              <Link to="/library" className="btn-ghost">
                More research
              </Link>
            </div>
          </div>
        </main>
      </div>

      {/* Inquiry */}
      <section
        id="inquiry"
        className="relative border-t border-[var(--border-soft)] py-28 mt-32"
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="font-mono-label mb-5">Inquiries</div>
            <h2 className="font-display-lg text-[var(--white)] text-4xl md:text-5xl leading-[1.06]">
              A private conversation.
            </h2>
            <p className="mt-7 text-[var(--slate)] leading-[1.85] max-w-md">
              Submissions are reviewed privately. We do not subscribe you to a
              list.
            </p>
          </div>
          <div className="md:col-span-7">
            <InquiryForm />
          </div>
        </div>
      </section>

      {/* Article-scoped styles */}
      <style>{`
        .article-list {
          list-style: none;
          padding: 0;
          margin: 18px 0 0 0;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .article-list li {
          position: relative;
          padding-left: 22px;
          color: var(--ivory-dim);
          line-height: 1.8;
          font-size: 16px;
        }
        .article-list li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 13px;
          width: 10px;
          height: 1px;
          background: var(--blue-soft);
        }
        .article-list li strong {
          color: var(--white);
          font-weight: 600;
        }
        .article-table {
          margin-top: 20px;
          border: 1px solid rgba(255,255,255,0.10);
        }
        .article-table-row {
          display: grid;
          grid-template-columns: 1.2fr 1fr 2fr;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .article-table-row:last-child {
          border-bottom: 0;
        }
        .article-table-row > div {
          padding: 18px 22px;
          font-size: 14.5px;
          color: var(--ivory-dim);
          line-height: 1.6;
        }
        .article-table-header > div {
          color: var(--slate-dim);
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          background: rgba(255,255,255,0.02);
        }
        .article-table-row > div:nth-child(2) {
          color: var(--white);
          font-variant-numeric: tabular-nums;
        }
        .article-link {
          color: var(--white);
          border-bottom: 1px solid var(--blue-soft);
          padding-bottom: 1px;
          transition: color 250ms ease;
        }
        .article-link:hover {
          color: var(--blue-soft);
        }
      `}</style>
    </Layout>
  );
};

/* --- subcomponent --- */
const Section = ({ n, icon: Icon, title, lede, children }) => (
  <section>
    <div className="flex items-center gap-3 mb-5">
      <span
        className="font-mono-label"
        style={{ color: "var(--blue-soft)" }}
      >
        {n}
      </span>
      <span
        className="h-px flex-1 max-w-[60px]"
        style={{ background: "rgba(255,255,255,0.12)" }}
      />
      <Icon size={15} className="text-[var(--slate)]" />
    </div>
    <h2
      className="font-display"
      style={{
        color: "var(--white)",
        fontSize: "clamp(28px, 3.4vw, 44px)",
        lineHeight: 1.1,
        marginBottom: "18px",
      }}
    >
      {title}
    </h2>
    {lede && (
      <p
        style={{
          color: "var(--ivory-dim)",
          fontSize: "17px",
          lineHeight: 1.8,
          marginBottom: "12px",
        }}
      >
        {lede}
      </p>
    )}
    <div
      className="space-y-5"
      style={{
        color: "var(--slate)",
        fontSize: "16px",
        lineHeight: 1.85,
      }}
    >
      {children}
    </div>
  </section>
);

export default ProfessionalPilots;
